import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import calendarIcon from "../../resources/icons/calendar.svg";
import calendarCheckIcon from "../../resources/icons/calendar_check.svg";
import cancelIcon from "../../resources/icons/cancel.svg";

interface ProjectCardProps {
  id: number;
  title: string;
  publishedAt: string;
  status: string;
  summary: string;
}

const getStatusStyle = (status: string) => {
  switch (status.toLowerCase()) {
    case "ongoing":
      return "text-[#B54708] bg-[#FFFAEB] border-[#FEDF89]";
    case "open":
      return "text-[#1672EC] bg-[#F0F9FF] border-[#B9E6FE]";
    case "draft":
      return "text-[#344054] bg-[#F9FAFB] border-[#EAECF0]";
    case "completed":
      return "text-[#067647] bg-[#ECFDF3] border-[#ABEFC6]";
    case "cancelled":
      return "text-[#B42318] bg-[#FEF3F2] border-[#FECDCA]";
    default:
      return "text-[#344054] bg-[#F9FAFB] border-[#D0D5DD]";
  }
};

const getStatusIcon = (status: string) => {
  switch (status.toLowerCase()) {
    case "completed":
      return calendarCheckIcon;
    case "cancelled":
      return cancelIcon;
    default:
      return calendarIcon;
  }
};

const getDateTitle = (status: string) => {
  switch (status.toLowerCase()) {
    case "completed":
      return "Completion";
    case "cancelled":
      return "Cancellation";
    case "draft":
      return "Creation";
    default:
      return "Publication";
  }
};

const ProjectCard: React.FC<ProjectCardProps> = ({
  id,
  title,
  publishedAt,
  status,
  summary,
}) => {
  const badgeStyle = getStatusStyle(status);
  const icon = getStatusIcon(status);
  const dateTitle = getDateTitle(status);

  const navigate = useNavigate();
  const location = useLocation();

  const onClick = () => {
    const userType = localStorage.getItem("userType");

    if (status === "completed" || status === "cancelled") {
      navigate(`/projects/${id}/finished`, {
        state: { from: location.pathname },
      });
    } else if (userType === "ASSIGNOR") {
      navigate(`/projects/${id}/publish`, {
        state: { from: location.pathname },
      });
    } else {
      navigate(`/projects/${id}/edit`, { state: { from: location.pathname } });
    }
  };

  return (
    <div
      onClick={onClick}
      className="h-[10rem] border border-[#EAECF0] rounded-lg p-4 bg-white shadow-sm space-y-3 flex-shrink-0 cursor-pointer"
    >
      <div className="text-[#101828] font-semibold text-base leading-5 line-clamp-1">
        {title}
      </div>
      <div className="flex justify-between items-center gap-2 whitespace-nowrap text-sm text-[#475467]">
        <div className="flex items-center text-xs">
          <img src={icon} alt="Calendar" className="w-4 h-4 shrink-0" />
          <span className="ml-1 truncate">
            {dateTitle} date: {publishedAt}
          </span>
        </div>
        <span
          className={`capitalize px-3 py-1 rounded-full text-xs font-medium border shrink-0 ${badgeStyle}`}
        >
          {status}
        </span>
      </div>
      <div className="border-t border-[#EAECF0] pt-2 text-xs text-[#344054] line-clamp-2">
        {summary}
      </div>
    </div>
  );
};

export default ProjectCard;
